/* eslint-disable react/jsx-props-no-spreading */
import { useForm } from '@folklore/forms';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { capitalizeFirstLetter } from '../../lib/utils';

import { useApi } from '../../contexts/ApiContext';
import { useUser } from '../../contexts/AuthContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import RoundedButton from '../buttons/Rounded';
import SubmitButton from '../buttons/Submit';
import FormGroup from '../fields/FormGroup';
import SelectField from '../fields/Select';
import TextField from '../fields/Text';
import TickIcon from '../icons/Tick';

import styles from '../../../styles/forms/edit-profile.module.scss';

const propTypes = {
    onComplete: PropTypes.func,
};

const defaultProps = {
    onComplete: null,
};

const EditProfileForm = ({ onComplete }) => {
    const route = useUrlGenerator();
    const intl = useIntl();
    const api = useApi();
    const user = useUser();
    const {
        provider = null,
        newsletter = false,
        id: userId = null,
        hasPassword = false,
    } = user || {};

    const hasPasswordField = provider === null || hasPassword;

    // console.log(provider === null, hasPassword);

    const update = useCallback(
        (action, data) => api.account.update({ ...(data || null), userId }),
        [api, userId],
    );
    const { fields, onSubmit, success, loading, setValue } = useForm({
        postForm: update,
        fields: hasPasswordField
            ? ['name', 'email', 'locale', 'password', 'password_confirmation']
            : ['name', 'email', 'locale'],
        value: user,
        ...(onComplete !== null ? { onComplete } : { onComplete: () => {} }),
    });

    // Updates user if it changes because parallel forms
    useEffect(() => {
        setValue(user);
    }, [newsletter]);

    return (
        <form className={styles.container} action={route('account.update')} onSubmit={onSubmit}>
            {provider !== null ? (
                <p className={styles.provider}>
                    <FormattedMessage
                        defaultMessage="Your profile is linked with {provider}"
                        description="Message"
                        values={{ provider: capitalizeFirstLetter(provider) }}
                    />
                    {hasPassword ? (
                        <FormattedMessage
                            defaultMessage=" and has a password"
                            description="Message"
                            values={{ provider: capitalizeFirstLetter(provider) }}
                        />
                    ) : null}
                </p>
            ) : null}
            <FormGroup
                {...fields.name}
                label={<FormattedMessage defaultMessage="Name" description="Field label" />}
                className={styles.formGroup}
                showLabel
            >
                <TextField
                    {...fields.name}
                    className={classNames(styles.input, styles.inputText)}
                    type="text"
                    required
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Your name',
                        description: 'Field placeholder',
                    })}
                />
            </FormGroup>
            <FormGroup
                {...fields.email}
                label={<FormattedMessage defaultMessage="Email" description="Field label" />}
                className={styles.formGroup}
                showLabel
            >
                <TextField
                    {...fields.email}
                    className={classNames(styles.input, styles.inputText)}
                    type="email"
                    required
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Your email',
                        description: 'Field placeholder',
                    })}
                />
            </FormGroup>
            <FormGroup
                {...fields.locale}
                label={
                    <FormattedMessage
                        defaultMessage="Communication language"
                        description="Field label"
                    />
                }
                className={styles.formGroup}
                showLabel
            >
                <SelectField
                    {...fields.locale}
                    className={classNames(styles.input, styles.inputText)}
                    options={[
                        {
                            label: intl.formatMessage({
                                defaultMessage: 'French',
                                description: 'Language',
                            }),
                            value: 'fr',
                        },
                        {
                            label: intl.formatMessage({
                                defaultMessage: 'English',
                                description: 'Language',
                            }),
                            value: 'en',
                        },
                    ]}
                />
            </FormGroup>
            {hasPasswordField ? (
                <FormGroup
                    {...fields.password}
                    label={
                        <FormattedMessage
                            defaultMessage="Update password"
                            description="Field label"
                        />
                    }
                    className={styles.formGroup}
                    showLabel
                >
                    <TextField
                        {...fields.password}
                        className={classNames(styles.input, styles.inputText)}
                        type="password"
                        placeholder={intl.formatMessage({
                            defaultMessage: 'New password',
                            description: 'Field placeholder',
                        })}
                    />
                    <TextField
                        {...fields.password_confirmation}
                        className={classNames(styles.input, styles.inputText)}
                        type="password"
                        placeholder={intl.formatMessage({
                            defaultMessage: 'New password confirmation',
                            description: 'Field placeholder',
                        })}
                    />
                </FormGroup>
            ) : null}

            {success ? (
                <div className={styles.success}>
                    <FormattedMessage
                        defaultMessage="Your profile information was updated successfully!"
                        description="Message"
                    />
                </div>
            ) : null}

            <div className={styles.formActions}>
                <SubmitButton
                    className={styles.formSubmitBtn}
                    icon={<TickIcon />}
                    loading={loading}
                >
                    <FormattedMessage defaultMessage="Save" description="Button label" />
                </SubmitButton>
                <RoundedButton className={styles.lightButton} href={route('account')} secondary>
                    <FormattedMessage defaultMessage="Back" description="Button label" />
                </RoundedButton>
            </div>
        </form>
    );
};
EditProfileForm.propTypes = propTypes;
EditProfileForm.defaultProps = defaultProps;

export default EditProfileForm;
